import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, RouterProvider, useLocation } from 'react-router';
import { useAuthStore } from '../features/auth/authStore.js';
import { LoginPage } from '../features/auth/LoginPage.js';
import { ProjectListPage } from '../features/projects/ProjectListPage.js';
import { AdminPage } from '../features/admin/AdminPage.js';
import { userMessage } from '../lib/errors.js';
import { UpdateBanner } from './UpdateBanner.js';
import { GlobalCalls } from './GlobalCalls.js';
import { useI18n } from '../i18n/i18n.js';

// The editor pulls in Three.js; loading it lazily keeps login and project list fast.
const EditorPage = lazy(() =>
  import('../features/editor/EditorPage.js').then((m) => ({ default: m.EditorPage })),
);

function RequireAuth({ children }: { children: ReactNode }) {
  const { m } = useI18n();
  const status = useAuthStore((s) => s.status);
  const location = useLocation();
  if (status === 'anonymous')
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (status === 'unknown') return <div className="page-message">{m.common.loading}</div>;
  return children;
}

function EditorLoading() {
  const { m } = useI18n();
  return <div className="page-message">{m.app.loadingEditor}</div>;
}

const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  {
    path: '/projects',
    element: (
      <RequireAuth>
        <ProjectListPage />
      </RequireAuth>
    ),
  },
  {
    path: '/projects/:projectId',
    element: (
      <RequireAuth>
        <Suspense fallback={<EditorLoading />}>
          <EditorPage />
        </Suspense>
      </RequireAuth>
    ),
  },
  {
    path: '/admin',
    element: (
      <RequireAuth>
        <AdminPage />
      </RequireAuth>
    ),
  },
  { path: '*', element: <Navigate to="/projects" replace /> },
]);

export function App() {
  const { m } = useI18n();
  const init = useAuthStore((s) => s.init);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    init().catch((err: unknown) => setError(userMessage(err)));
  }, [init]);

  if (error) {
    return (
      <div className="page-message error">
        {error}
        <button type="button" className="btn" onClick={() => window.location.reload()}>
          {m.common.retry}
        </button>
      </div>
    );
  }
  return (
    <>
      <RouterProvider router={router} />
      <GlobalCalls navigate={(path) => void router.navigate(path)} />
      <UpdateBanner />
    </>
  );
}
